import _ from "underscore";
import BaseControl from "../base-control";
import { createRef } from "react";
import ReactDOM from "react-dom/client";
import { StriimTheme, StriimFormBuilder, FIELD_TYPES } from "@striim/striim-ui";
import App from "app";
import FileBrowser from "../../../../../../src/generic/filebrowser-v2/filebrowser";
import { convertNameToHumanRedable } from "app/components/common/editor/control-resource-resolver";
import ConnectionProfileSelector from "../../../../../../src/generic/connection-profile-selector/connection-profile-selector";
import SourceInfoComponent from "src/modules/wizards/common/source-info-message-box/source-info-box.jsx";
import { PRESERVED_NAMES } from "core/constants";

var Model = BaseControl.Model.extend({
    defaults: {
        value: null
    }
});

var View = BaseControl.View.extend({
    template: _.template(`<div>   </div>`),

    getViewValue: function() {
        return this.model.get("value"); // No op, we're doing it in react.
    },

    setViewValue: function() {
        // No op, we're doing it in react.
    },
    showError: function(errors) {
        const errorBox = $("#dirty-fields");
        const errorBoxContainer = $("#error-box");
        if (!errors || _.isEmpty(errors)) {
            errorBoxContainer.hide();
        } else {
            errorBoxContainer.show();
            const getErrorFieldLabels = errorFields => {
                let labels = errorFields.map(field => this.labelMap[field]);
                return labels.join(", ");
            };
            errorBox.html(getErrorFieldLabels(Object.keys(errors)));
        }
    },
    valueChanged: function(value) {
        const touched = this.options.populateModel.get("__touched__") || {};
        Object.keys(value)?.forEach(val => {
            if (value[val] !== null && typeof value[val] === "object") {
                this.options.populateModel.set(val, value[val].value);
            } else {
                this.options.populateModel.set(val, value[val]);
            }
            touched[val] = true;
        });
        this.options.populateModel.set("__touched__", touched, { silent: true });
        this.forceChanged();
    },

    initialize: function(options) {
        this.value = options.populateModel.previousAttributes();
        // Fields the user has explicitly changed — skip applying form defaults to these
        // so back-navigation doesn't undo intentional choices (e.g. disabling useConnectionProfile).
        const touched = options.populateModel.get("__touched__") || {};

        options.formFields.forEach(field => {
            if (
                field.defaultValue !== undefined &&
                !touched[field.name] &&
                (this.value[field.name] === undefined ||
                    // added to make sure the defaults given in source-form-info is used, otherwise the model defaults take precedence.
                    // for eg useConnectionProfile has default value of true in source-form-info.js but false in models.js
                    this.value[field.name] === options.populateModel.defaults[field.name])
            ) {
                this.value[field.name] = field.defaultValue;
                options.populateModel.set(field.name, field.defaultValue, { silent: true });
            }
        });

        App.vent.trigger("appwizard:back:hide");
        App.vent.trigger("appwizard:next:enable");
        this.labelMap = {};
        options.formFields.forEach(field => {
            this.labelMap[field.name] = field?.label ?? convertNameToHumanRedable(field.name);
        });
    },
    validate: async function() {
        this?.formRef?.current.submitForm();
        const errors = await this?.formRef?.current.validateForm();
        this.showError(errors);
        return _.isEmpty(errors);
    },
    onShow: function() {
        try {
            const root = ReactDOM.createRoot(this.$el[0]);
            const formRef = createRef();
            this.formRef = formRef;
            root.render(
                <WizardSourceFormComponent
                    enabled={this.model.get("enabled")}
                    formFields={this.options.formFields}
                    hasAutoSchemaConversion={this.options.hasAutoSchemaConversion}
                    isAutomatedWizard={this.options.isAutomatedILCDC}
                    value={this.value}
                    onChange={value => this.valueChanged(value)}
                    formRef={formRef}
                    sourceID={this.options.sourceID}
                />
            );
        } catch (e) {
            console.error("Error rendering SalesforceReaderWizardEditor", e);
        }
    }
});

function buildTextField() {
    return _.extend({}, BaseControl, {
        View: View,
        Model: Model
    });
}

export default buildTextField();

export const WizardSourceFormComponent = props => {
    const {
        hasAutoSchemaConversion,
        isAutomatedWizard,
        value,
        onChange,
        formFields,
        formRef,
        sourceID,
        setFormValid
    } = props;
    return (
        <StriimTheme>
            <SourceInfoComponent sourceID={sourceID} />
            <StriimFormBuilder
                formProps={{ hasAutoSchemaConversion, isAutomatedWizard }}
                onChange={onChange}
                initialFieldValues={value}
                data={formFields}
                validateOnChange={true}
                setFormValid={setFormValid ?? (() => {})}
                formRef={formRef}
                customFields={{
                    [FIELD_TYPES.FILE]: props => <FileBrowser directoryOnly={false} {...props} />,
                    [FIELD_TYPES.CONNECTIONPROFILE]: props => (
                        <ConnectionProfileSelector useMenuPortalTarget={true} {...props} />
                    )
                }}
                preservedNames={PRESERVED_NAMES}
            />
        </StriimTheme>
    );
};
